import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Sun,
  Moon,
  Plane,
  Star,
  Clock,
  CheckCircle2,
  AlertTriangle,
  HelpCircle,
  TrendingUp,
  Sparkles,
  ArrowRight,
  ShieldCheck,
  Wifi,
  Coffee,
  Utensils,
  Briefcase,
  Layers,
  Info,
  ChevronRight,
  Award,
  DollarSign,
  ArrowDown,
  ChevronLeft,
  Ticket,
  MapPin,
  Armchair,
} from "lucide-react";
import { getFlightDetailsList } from "../../../services/Front/flightService";
import { notify } from "../../../components/Common/Alerts";
import Utils from "../../../utils";
import { decryptData } from "../../../utils/crypto";

const RedemptionDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  let data = location.state;
  const [flightData, setFlightData] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!data) return;
    const obj = {
      search_id: data.search_id,
      card_id: data.card_id,
      flight_id: data.flight_id,
    };
    setLoading(true);

    getFlightDetailsList(obj)
      .then((response) => {
        const decrypted = decryptData(response.data.data);
        const apiData = decrypted?.data || {};

        const mappedData = {
          ...apiData,

          // ================= HERO =================
          airline_rating: apiData.confidence_score
            ? (apiData.confidence_score / 20).toFixed(1)
            : "4.5",

          aircraft: apiData.flight_segments?.[0]?.airplane || "Aircraft",

          route: {
            from:
              apiData.flight_segments?.[0]?.departure_airport?.id ||
              apiData.flight_segments?.[0]?.departure_airport ||
              "ORD",

            to:
              apiData.flight_segments?.[apiData.flight_segments.length - 1]
                ?.arrival_airport?.id ||
              apiData.flight_segments?.[apiData.flight_segments.length - 1]
                ?.arrival_airport ||
              "DEL",
          },

          duration: apiData.total_duration,

          ai_insight:
            apiData.explanation?.cash_vs_points_hint ||
            "Excellent redemption value with available points.",

          // ================= WALLET =================
          reward_wallet: {
            card_name: "American Express Membership Rewards",
            reward_program: "Membership Rewards",
            progress_percent: Math.min(
              100,
              Math.round(
                (apiData.required_points / apiData.available_points) * 100,
              ),
            ),
          },

          // ================= PREMIUM TARGETS =================
          premium_cabin_targets: {
            business_class: {
              ...apiData.premium_cabin_targets?.business_class,
              recommendation:
                apiData.available_points >=
                apiData.premium_cabin_targets?.business_class?.estimated_points
                  ? "Ready for Business Class Upgrade"
                  : "Need More Points",
              progress_percent: Math.min(
                100,
                Math.round(
                  (apiData.available_points /
                    apiData.premium_cabin_targets?.business_class
                      ?.estimated_points) *
                    100,
                ) || 0,
              ),
            },

            first_class: {
              ...apiData.premium_cabin_targets?.first_class,
              recommendation:
                apiData.available_points >=
                apiData.premium_cabin_targets?.first_class?.estimated_points
                  ? "Ready for First Class Upgrade"
                  : "Need More Points",
              progress_percent: Math.min(
                100,
                Math.round(
                  (apiData.available_points /
                    apiData.premium_cabin_targets?.first_class
                      ?.estimated_points) *
                    100,
                ) || 0,
              ),
            },
          },

          // ================= TRANSFER PARTNERS (SAFE FIX) =================
          transfer_partners: (apiData.transfer_partners || []).map(
            (partner) => ({
              ...partner,
              transfer_time: "Instant",
              transfer_efficiency:
                partner.value_score >= 50 ? "High" : "Medium",
            }),
          ),

          best_transfer_recommendation: {
            ...apiData.best_transfer_recommendation,
            from_program:
              apiData.best_transfer_recommendation?.from_card_program ||
              apiData.best_transfer_recommendation?.from_program ||
              "Your Card",

            to_program:
              apiData.best_transfer_recommendation?.airline_program ||
              apiData.best_transfer_recommendation?.to_program ||
              "Partner Program",

            smart_transfer_insight:
              apiData.best_transfer_recommendation?.insight?.reason ||
              apiData.best_transfer_recommendation?.reason ||
              "Good redemption option",
          },

          // ================= HIGHLIGHTS =================
          luxury_highlights: [
            `${apiData.cabin} cabin experience`,
            `${apiData.stops} stop itinerary`,
            `${apiData.required_points.toLocaleString()} points required`,
            `${apiData.value_score}/100 redemption value score`,
            `${((apiData.cpp_real || 0) * 100).toFixed(2)}¢ per point value`,
          ],

          // ================= RECOMMENDATIONS =================
          smart_recommendations: [
            apiData.explanation?.best_action,
            apiData.explanation?.cash_vs_points_hint,
            apiData.explanation?.warning,
          ].filter(Boolean),

          // ================= CABIN =================
          cabin_experience: {
            seat_type: apiData.cabin,
            sleep_score:
              apiData.cabin === "Business"
                ? 9
                : apiData.cabin === "Premium Economy"
                  ? 7
                  : 6,
          },

          // ================= SAVINGS =================
          savings_analysis: {
            cash_price: apiData.price,
            estimated_savings: (apiData.price || 0) - (apiData.taxes || 0),
            cpp: (
              ((apiData.net_price || 0) / (apiData.required_points || 1)) *
              100
            ).toFixed(2),
          },

          // ================= SAFE FLAGS =================
          booking_confidence: `${apiData.confidence_score || 0}%`,
        };

        setFlightData(mappedData);
        setLoading(false);
      })
      .catch((err) => {
        notify.error(Utils.getErrorMessage(err));
        setLoading(false);
      });
  }, [data]);

  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 py-8 animate-pulse space-y-6">
        {/* HERO SKELETON */}
        <div className="h-[260px] rounded-3xl bg-slate-200 dark:bg-slate-800" />

        {/* INFO BANNER */}
        <div className="h-20 rounded-2xl bg-slate-200 dark:bg-slate-800" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* LEFT SKELETON */}
          <div className="lg:col-span-8 space-y-6">
            <div className="h-[200px] rounded-2xl bg-slate-200 dark:bg-slate-800" />
            <div className="h-[300px] rounded-2xl bg-slate-200 dark:bg-slate-800" />
            <div className="h-[250px] rounded-2xl bg-slate-200 dark:bg-slate-800" />
            <div className="h-[200px] rounded-2xl bg-slate-200 dark:bg-slate-800" />
          </div>

          {/* RIGHT SIDEBAR SKELETON */}
          <div className="lg:col-span-4 space-y-6">
            <div className="h-[280px] rounded-3xl bg-slate-200 dark:bg-slate-800" />
            <div className="h-[200px] rounded-3xl bg-slate-200 dark:bg-slate-800" />
            <div className="h-[120px] rounded-2xl bg-slate-200 dark:bg-slate-800" />
          </div>
        </div>
      </div>
    );
  }
  
  const formatDuration = (mins) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h}h ${m}m`;
  };

  const formatDateLabel = (dateTimeStr) => {
    if (!dateTimeStr) return "";
    const d = new Date(dateTimeStr.replace(" ", "T"));
    return d.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatTimeLabel = (dateTimeStr) => {
    if (!dateTimeStr) return "";
    return dateTimeStr.split(" ")[1];
  };

  const getLayoverDuration = (arrivalTime, departureTime) => {
    const arrival = new Date(arrivalTime.replace(" ", "T"));
    const departure = new Date(departureTime.replace(" ", "T"));

    const diffMinutes = Math.floor((departure - arrival) / (1000 * 60));

    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;

    return `${hours}h ${minutes}m`;
  };

  const bestTransfer = flightData?.best_transfer_recommendation;

  const getTransferStatus = (status) => {
    switch (status) {
      case "GOOD_OPTION":
        return {
          label: "Recommended",
          color: "bg-emerald-50 text-emerald-600",
        };

      case "BEST_OPTION":
        return {
          label: "Best Choice",
          color: "bg-[#D4AF37]/10 text-[#D4AF37]",
        };

      default:
        return {
          label: status,
          color: "bg-slate-100 text-slate-600",
        };
    }
  };

  return (
    <div
      className={`${darkMode ? "dark bg-[#080C14]" : "bg-[#FAFAFC]"} min-h-screen w-full transition-colors duration-300 pb-16 font-sans antialiased`}
    >
      <div className="hidden md:block w-full border-b border-slate-200/80 dark:border-slate-800/60 bg-white dark:bg-[#111823] transition-colors duration-300">
        <div className="max-w-[1400px] mx-auto px-4 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="hidden md:flex flex items-center gap-2 text-xs font-black uppercase tracking-wider text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors cursor-pointer"
          >
            <ChevronLeft size={20} />
            <span>Back to search results</span>
          </button>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-8 space-y-6">
            <div className="relative rounded-3xl overflow-hidden bg-gradient-to-r from-slate-950 to-slate-900 text-white border border-slate-800 shadow-xl">
              <div
                className="absolute inset-0 opacity-25 mix-blend-luminosity bg-cover bg-center"
                style={{
                  backgroundImage: `url('https://images.unsplash.com/photo-1512453979798-5ea266f8880c?q=80&w=1200&auto=format&fit=crop')`,
                }}
              />

              <div className="relative p-6 sm:p-8 z-10 flex flex-col justify-between h-full min-h-[260px]">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-red-600 rounded-xl p-1.5 flex items-center justify-center shrink-0">
                      <img
                        src={flightData?.airline_logo}
                        alt={flightData?.airline}
                        className="w-full h-full object-contain invert brightness-0"
                      />
                    </div>
                    <div>
                      <h2 className="text-xl font-black tracking-tight">
                        {flightData?.airline} Airlines
                      </h2>
                      <div className="flex items-center gap-2 mt-0.5 text-xs font-bold text-slate-400">
                        <span className="flex items-center gap-0.5 text-amber-400">
                          <Star size={12} fill="currentColor" />{" "}
                          {flightData?.airline_rating}
                        </span>
                        <span>•</span>
                        <span className="px-2 py-0.5 bg-[#D4AF37]/20 border border-[#D4AF37]/30 text-[#D4AF37] rounded-md text-[10px] uppercase font-black tracking-wider">
                          {flightData?.luxury_index
                            ?.replace(/_/g, " ")
                            .toLowerCase()
                            .replace(/\b\w/g, (c) => c.toUpperCase())}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-xs font-bold text-slate-300">
                    <span className="flex items-center gap-1.5">
                      🛄 {flightData?.cabin} Class
                    </span>
                    <span>•</span>
                    <span className="flex items-center gap-1.5">
                      ✈️ {flightData?.aircraft}
                    </span>
                  </div>
                </div>

                <div className="my-8 flex items-center justify-between relative max-w-xl">
                  <div className="space-y-1">
                    <div className="text-3xl sm:text-4xl font-black tracking-tight">
                      {flightData?.route?.from}
                    </div>
                    <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                      {formatTimeLabel(flightData?.departure_time)}
                    </div>
                    <div className="text-[10px] font-medium text-slate-500">
                      {formatDateLabel(flightData?.departure_time)}
                    </div>
                  </div>

                  <div className="flex-1 flex flex-col items-center px-4 relative group">
                    <span className="text-[10px] font-extrabold text-[#D4AF37] uppercase tracking-widest mb-1.5 transition-all">
                      {formatDuration(flightData?.duration)}
                    </span>
                    <div className="w-full flex items-center justify-center relative">
                      <div className="absolute left-0 right-0 h-px border-t border-dashed border-slate-700" />
                      <div className="p-1.5 bg-slate-900 border border-slate-700 rounded-full z-10 text-[#D4AF37]">
                        <Plane size={14} className="transform rotate-90" />
                      </div>
                    </div>
                    <span className="text-[10px] font-bold text-slate-500 mt-1.5">
                      {flightData?.stops === 0
                        ? "Nonstop"
                        : `${flightData?.stops} stops`}
                    </span>
                  </div>

                  <div className="space-y-1 text-right">
                    <div className="text-3xl sm:text-4xl font-black tracking-tight">
                      {flightData?.route?.to}
                    </div>
                    <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                      {formatTimeLabel(flightData?.arrival_time)}
                    </div>
                    <div className="text-[10px] font-medium text-slate-500">
                      {formatDateLabel(flightData?.arrival_time)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-5 bg-[#D4AF37]/5 dark:bg-[#D4AF37]/5 border border-[#D4AF37]/20 dark:border-[#D4AF37]/10 rounded-2xl grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
              <div className="md:col-span-4 space-y-0.5">
                <div className="flex items-center gap-2 text-xs font-black text-[#D4AF37] tracking-widest uppercase">
                  <Sparkles size={14} className="animate-pulse" /> Why is this
                  great?
                </div>
                <p className="text-[11px] font-semibold text-slate-400 dark:text-slate-500">
                  System verified point metrics
                </p>
              </div>
              <div className="md:col-span-8 flex flex-col sm:flex-row gap-4 justify-between sm:items-center">
                <p className="text-xs font-bold text-slate-700 dark:text-slate-300 leading-relaxed max-w-md">
                  "{flightData?.booking_recommendation}"
                </p>
                <div className="text-xs font-bold text-emerald-600 dark:text-emerald-400 shrink-0 bg-emerald-500/10 px-2.5 py-1 rounded-lg border border-emerald-500/10">
                  🔥 Smart Redemption Pick
                </div>
              </div>
            </div>

            <div className="space-y-0">
              {flightData?.flight_segments?.map((seg, idx) => {
                const nextSeg = flightData?.flight_segments[idx + 1];

                const layover =
                  nextSeg &&
                  getLayoverDuration(
                    seg.arrival_airport.time,
                    nextSeg.departure_airport.time,
                  );

                return (
                  <React.Fragment key={idx}>
                    {/* Flight Card */}
                    <div className="relative flex gap-5">
                      {/* Timeline */}
                      <div className="flex flex-col items-center">
                        <div className="w-4 h-4 rounded-full bg-emerald-500 border-4 border-white shadow" />

                        <div className="w-[2px] flex-1 bg-slate-200 min-h-[140px]" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 pb-8">
                        <div className="bg-white dark:bg-[#111823] border rounded-2xl p-5 shadow-sm hover:shadow-md transition">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="text-lg font-black">
                                {seg.departure_airport.id}
                              </div>

                              <div className="text-xs text-slate-500">
                                {seg.departure_airport.name}
                              </div>

                              <div className="text-2xl font-black mt-2">
                                {formatTimeLabel(seg.departure_airport.time)}
                              </div>
                            </div>

                            <div className="text-center">
                              <div className="text-xs font-bold text-[#D4AF37]">
                                {formatDuration(seg.duration)}
                              </div>

                              <Plane
                                size={18}
                                className="mx-auto my-2 text-[#D4AF37]"
                              />

                              <div className="text-xs font-bold">
                                {seg.flight_number}
                              </div>

                              <div className="text-[11px] text-slate-500">
                                {seg.airplane}
                              </div>
                            </div>

                            <div className="text-right">
                              <div className="text-lg font-black">
                                {seg.arrival_airport.id}
                              </div>

                              <div className="text-xs text-slate-500">
                                {seg.arrival_airport.name}
                              </div>

                              <div className="text-2xl font-black mt-2">
                                {formatTimeLabel(seg.arrival_airport.time)}
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-2 mt-4">
                            {seg.extensions?.slice(0, 4).map((item, i) => (
                              <span
                                key={i}
                                className="px-2 py-1 rounded-full bg-slate-100 text-[10px] font-semibold"
                              >
                                {item}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Layover */}
                    {nextSeg && (
                      <div className="relative flex gap-5">
                        <div className="flex flex-col items-center">
                          <div className="w-5 h-5 rounded-full bg-amber-400 border-4 border-white shadow-lg" />
                          <div className="w-[2px] h-24 bg-slate-200" />
                        </div>

                        <div className="flex-1 pb-8">
                          <div className="rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50 p-5">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="text-xs font-black text-amber-700 uppercase tracking-widest">
                                  Layover
                                </div>

                                <div className="text-xl font-black mt-1">
                                  {layover}
                                </div>

                                <div className="text-sm text-slate-600 mt-1">
                                  {seg.arrival_airport.name}
                                </div>
                              </div>

                              <div className="text-right">
                                <div className="text-3xl">🏛️</div>

                                <div className="text-xs font-semibold text-slate-500">
                                  Connection Stop
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}

              {/* Final Arrival */}
              <div className="relative flex gap-5">
                <div className="flex flex-col items-center">
                  <div className="w-5 h-5 rounded-full bg-red-500 border-4 border-white shadow-lg" />
                </div>

                <div className="pb-4">
                  <div className="text-xs font-black uppercase text-red-500 tracking-widest">
                    Final Arrival
                  </div>

                  <div className="text-lg font-black">
                    {
                      flightData?.flight_segments[
                        flightData?.flight_segments.length - 1
                      ].arrival_airport.name
                    }
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
              <div className="md:col-span-4 p-5 bg-white dark:bg-[#111823] border border-slate-200/80 dark:border-slate-800/80 rounded-2xl flex flex-col justify-between shadow-xs">
                <div>
                  <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-4">
                    Your Reward Wallet
                  </h3>
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center text-white font-black text-xs shrink-0">
                      AX
                    </div>
                    <div>
                      <h4 className="text-xs font-black text-slate-900 dark:text-white leading-none">
                        {flightData?.reward_wallet.card_name}
                      </h4>
                      <p className="text-[10px] font-bold text-slate-400 mt-1">
                        {flightData?.reward_wallet.reward_program}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 space-y-2">
                  <div className="w-full bg-slate-100 dark:bg-slate-800 h-1.5 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 rounded-full"
                      style={{
                        width: `${flightData?.reward_wallet.progress_percent}%`,
                      }}
                    />
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="text-xl font-black text-slate-900 dark:text-white tracking-tight">
                      {Number(flightData?.available_points).toLocaleString()}
                    </span>
                    <span className="text-[10px] font-bold text-slate-400">
                      Available Points
                    </span>
                  </div>
                </div>
              </div>

              <div className="md:col-span-8 p-5 bg-white dark:bg-[#111823] border border-slate-200/80 dark:border-slate-800/80 rounded-2xl shadow-xs">
                <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-4">
                  Premium Cabin Targets
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {Object.entries(flightData?.premium_cabin_targets || {}).map(
                    ([key, target]) => (
                      <div
                        key={key}
                        className="p-3.5 bg-slate-50 dark:bg-[#161F2E] border border-slate-200/60 dark:border-slate-800/50 rounded-xl flex items-center justify-between gap-2"
                      >
                        <div className="space-y-1">
                          <h4 className="text-xs font-black uppercase text-slate-900 dark:text-white tracking-wide">
                            {key.replace(/_/g, " ")}
                          </h4>
                          <div className="text-sm font-black text-[#D4AF37]">
                            {Number(target?.estimated_points).toLocaleString()}{" "}
                            <span className="text-[10px] font-medium text-slate-400">
                              pts
                            </span>
                          </div>
                          <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 leading-tight">
                            {target?.recommendation}
                          </p>
                        </div>

                        {/* Dynamic Upgrade Target Ring SVG gauge component */}
                        <div className="relative w-11 h-11 flex items-center justify-center shrink-0">
                          <svg
                            className="w-full h-full transform -rotate-90"
                            viewBox="0 0 36 36"
                          >
                            <path
                              className="text-slate-200 dark:text-slate-800"
                              strokeWidth="3"
                              stroke="currentColor"
                              fill="none"
                              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            />
                            <path
                              className="text-[#D4AF37]"
                              strokeWidth="3"
                              strokeDasharray={`${target.progress_percent}, 100`}
                              strokeLinecap="round"
                              stroke="currentColor"
                              fill="none"
                              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            />
                          </svg>
                          <span className="absolute text-[10px] font-black text-slate-800 dark:text-white">
                            {target.progress_percent}%
                          </span>
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </div>
            </div>

            {/* DYNAMIC TRANSFER PARTNERS CONVERSION MODULE NETWORK */}
            <div className="space-y-6">
              {/* ================= BEST TRANSFER CARD ================= */}
              <div className="rounded-2xl overflow-hidden border border-voyage-cyan bg-gradient-to-r from-voyage-cyan via-white to-voyage-cyan-50 shadow-sm">
                {/* Header */}
                <div className="px-4 py-2 bg-voyage-cyan text-white flex items-center justify-between">
                  <span className="font-black text-xs tracking-widest uppercase">
                    🏆 Recommended Transfer
                  </span>
                  <span className="text-[10px] font-bold">
                    Best Booking Path
                  </span>
                </div>

                {/* Body */}
                <div className="p-5">
                  {/* Route */}
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs text-slate-500">✈️</div>
                      <div className="font-black text-lg">
                        {flightData?.best_award_option?.airline || "N/A"}
                      </div>
                    </div>

                    <ArrowRight size={22} className="text-emerald-500" />

                    <div className="text-right">
                      <div className="text-xs text-slate-500">
                        <Ticket size={12} />
                      </div>
                      <div className="font-black text-lg">
                        {flightData?.best_award_option?.program_name || "N/A"}
                      </div>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-3 mt-5">
                    <div className="bg-white rounded-xl border p-3 text-center">
                      <div className="text-xl font-black text-emerald-600">
                        {Number(
                          flightData?.best_award_option?.points_required || 0,
                        ).toLocaleString()}
                      </div>
                      <div className="text-[10px] uppercase text-slate-500">
                        Est. Points
                      </div>
                    </div>

                    <div className="bg-white rounded-xl border p-3 text-center">
                      <div className="text-sm font-black text-emerald-600">
                        <MapPin size={12} />
                        {flightData?.best_award_option?.cabin}
                      </div>
                    </div>
                  </div>

                  {/* Insight */}
                  <div className="mt-4 flex items-center gap-2 text-sm font-semibold text-emerald-700">
                    ✓ {bestTransfer?.insight?.reason}
                  </div>
                </div>
              </div>
            </div>

            <div className="md:col-span-4 p-6 bg-white dark:bg-[#111823] border border-slate-200/70 dark:border-slate-800/70 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                  Points Transfer Recommendation
                </h3>

                <div className="w-9 h-9 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                  <Award size={16} className="text-[#D4AF37]" />
                </div>
              </div>

              {/* Transfer Flow */}
              <div className="flex flex-col items-center text-center">
                <div className="w-full p-3 rounded-xl bg-slate-50 dark:bg-[#161F2E] border">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400">
                    From
                  </p>
                  <h4 className="font-bold text-slate-900 dark:text-white">
                    {bestTransfer?.from_card_program}
                  </h4>
                </div>

                <ArrowDown className="my-3 text-[#D4AF37]" size={18} />

                <div className="w-full p-3 rounded-xl bg-[#D4AF37]/5 border border-[#D4AF37]/20">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400">
                    Transfer To
                  </p>
                  <h4 className="font-bold text-slate-900 dark:text-white">
                    {bestTransfer?.airline_program}
                  </h4>
                </div>
              </div>

              {/* Details */}
              <div className="mt-5 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500 flex items-center gap-2">
                    ✈ Airline
                  </span>
                  <span className="text-sm font-semibold">
                    {bestTransfer?.airline_name}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500 flex items-center gap-2">
                    <Armchair size={13} />
                    Cabin
                  </span>
                  <span className="text-sm font-semibold">
                    {bestTransfer?.cabin}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">
                    Points Required
                  </span>

                  <div className="px-3 py-1 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20">
                    <span className="font-bold text-[#D4AF37]">
                      {Number(
                        bestTransfer?.transferable_points,
                      ).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* DUAL CONFIG OVERVIEW ROW CARD: HIGHLIGHTS & RECOMMENDATIONS */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Luxury Highlights Section (Includes sample hard product dashboard preview panel image matching image_b5aaa4.png) */}
              <div className="p-5 bg-white dark:bg-[#111823] border border-slate-200/80 dark:border-slate-800/80 rounded-2xl shadow-xs space-y-4">
                <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">
                  Highlights & Insights
                </h3>
                <div className="relative rounded-xl overflow-hidden h-36 bg-slate-100 dark:bg-slate-800">
                  <img
                    src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?q=80&w=600&auto=format&fit=crop"
                    alt="Cabin Hard Product Layout Panel"
                    className="w-full h-full object-cover brightness-90 mix-blend-multiply dark:mix-blend-normal"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent p-4 flex flex-col justify-end">
                    <span className="text-[9px] font-black text-[#D4AF37] uppercase tracking-widest">
                      Cabin Framework Configuration
                    </span>
                    <h4 className="text-xs font-bold text-white mt-0.5">
                      {flightData?.cabin_experience.seat_type} verified
                    </h4>
                  </div>
                </div>

                <ul className="space-y-2">
                  {flightData?.luxury_highlights.map((highlight, idx) => (
                    <li
                      key={idx}
                      className="flex items-center gap-2 text-xs font-semibold text-slate-600 dark:text-slate-300"
                    >
                      <span className="text-[#D4AF37]">★</span> {highlight}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Smart Route System Rules Recommendations */}
              <div className="p-5 bg-white dark:bg-[#111823] border border-slate-200/80 dark:border-slate-800/80 rounded-2xl shadow-xs flex flex-col justify-between">
                <div>
                  <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-4">
                    Smart Recommendations
                  </h3>
                  <ul className="space-y-3">
                    {flightData?.smart_recommendations.map((rec, idx) => (
                      <li
                        key={idx}
                        className="flex items-start gap-2.5 p-3 bg-slate-50 dark:bg-[#161F2E] border border-slate-100 dark:border-slate-800 rounded-xl"
                      >
                        <Info
                          size={14}
                          className="text-[#D4AF37] shrink-0 mt-0.5"
                        />
                        <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 leading-normal">
                          {rec}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Earning Hints Mini Metric Bar Block Footer */}
                <div className="pt-4 border-t border-slate-100 dark:border-slate-800/60 grid grid-cols-2 gap-2 text-[10px] font-bold text-slate-400">
                  <span className="flex items-center gap-1">
                    💡 Track transfer offers
                  </span>
                  <span className="flex items-center gap-1">
                    💳 Use card multipliers
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* ================= RIGHT SIDEBAR WIDGET COLUMN (Matches image_b5aaa4.png Right Bar Layout) ================= */}
          <div className="lg:col-span-4 space-y-6 lg:sticky lg:top-6">
            {/* PRIMARY BOOKING CONVERSION VALUE WRAPPER COMPONENT FRAME */}
            <div className="bg-slate-900 text-white rounded-3xl p-6 border border-slate-800 shadow-xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-black">
                    Redemption Analysis
                  </p>

                  <h3 className="text-2xl font-black mt-2">
                    {Number(flightData?.required_points).toLocaleString()} pts
                  </h3>

                  <p className="text-slate-400 text-sm mt-1">
                    + ${flightData?.taxes} taxes & fees
                  </p>
                </div>

                <div
                  className={`px-3 py-2 rounded-xl text-xs font-bold ${
                    flightData?.can_book_now
                      ? "bg-emerald-500/20 text-emerald-400"
                      : "bg-red-500/20 text-red-400"
                  }`}
                >
                  {flightData?.can_book_now ? "Book Now" : "Not Available"}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 mt-6">
                <div className="bg-white/5 rounded-xl p-3 text-center">
                  <div className="text-[10px] text-slate-400 uppercase">
                    Value Score
                  </div>
                  <div className="text-lg font-black text-[#D4AF37]">
                    {flightData?.value_score}
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-3 text-center">
                  <div className="text-[10px] text-slate-400 uppercase">
                    CPP
                  </div>
                  <div className="text-lg font-black">
                    {(flightData?.cpp_real * 100).toFixed(2)}¢
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-3 text-center">
                  <div className="text-[10px] text-slate-400 uppercase">
                    Confidence
                  </div>
                  <div className="text-lg font-black text-emerald-400">
                    {flightData?.confidence_score}%
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-slate-800">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Available Points</span>
                  <span className="font-bold">
                    {Number(flightData?.available_points).toLocaleString()}
                  </span>
                </div>

                <div className="flex justify-between text-sm mt-2">
                  <span className="text-slate-400">Required</span>
                  <span className="font-bold">
                    {Number(flightData?.required_points).toLocaleString()}
                  </span>
                </div>

                <div className="mt-3 w-full bg-slate-800 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-emerald-500 h-full"
                    style={{
                      width: `${Math.min(
                        (flightData?.required_points /
                          flightData?.available_points) *
                          100,
                        100,
                      )}%`,
                    }}
                  />
                </div>
              </div>
            </div>

            {/* HOVER EXPERIENCE AMENITY SLEEP SCORE SNAPSHOT COMPONENT */}
            <div className="p-5 bg-white dark:bg-[#111823] border border-slate-200/80 dark:border-slate-800/80 rounded-3xl shadow-xs space-y-4">
              <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 flex items-center gap-1">
                🛋️ Premium Cabin Attributes
              </h3>

              <div className="grid grid-cols-2 gap-y-3 gap-x-4">
                <div className="flex items-center gap-2 text-xs font-bold text-slate-600 dark:text-slate-300">
                  <Wifi size={14} className="text-slate-400" />
                  <span>Wi-Fi Onboard</span>
                </div>
                <div className="flex items-center gap-2 text-xs font-bold text-slate-600 dark:text-slate-300">
                  <Coffee size={14} className="text-slate-400" />
                  <span>Lounge Access</span>
                </div>
                <div className="flex items-center gap-2 text-xs font-bold text-slate-600 dark:text-slate-300">
                  <Utensils size={14} className="text-slate-400" />
                  <span>Premium Dining</span>
                </div>
                <div className="flex items-center gap-2 text-xs font-bold text-slate-600 dark:text-slate-300">
                  <Briefcase size={14} className="text-slate-400" />
                  <span>Amenity Kit</span>
                </div>
              </div>

              <div className="pt-3 border-t border-slate-100 dark:border-slate-800/60 flex items-center justify-between">
                <span className="text-[10px] font-black uppercase tracking-wide text-slate-400">
                  Ergonomic Sleep Score
                </span>
                <span className="text-xs font-black text-slate-900 dark:text-white bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-md">
                  {flightData?.cabin_experience.sleep_score} / 10
                </span>
              </div>
            </div>

            {/* CONFIDENCE FOOTER LEGAL BADGE BAR */}
            <div className="p-4 bg-emerald-500/5 border border-emerald-500/10 rounded-2xl flex gap-3 items-start">
              <ShieldCheck
                size={18}
                className="text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5"
              />
              <div className="space-y-0.5">
                <h4 className="text-[10px] font-black uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                  Route Availability Shield Secured
                </h4>
                <p className="text-[11px] font-medium text-slate-500 dark:text-slate-400 leading-normal">
                  Historical flight pricing curves confirm value matrix
                  stability for scheduled travel metrics in Q3 2026.
                </p>
              </div>
            </div>
            <div className="space-y-3 mt-4">
              <button
                onClick={() => navigate("/search")}
                className="w-full h-11 rounded-2xl bg-transparent hover:bg-white/[0.03] text-[#D4AF37] border border-[#D4AF37]/20 font-bold tracking-wide text-xs transition-all duration-300 cursor-pointer"
              >
                ✨ Unlock More Premium Flight Deals
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RedemptionDetails;
